import {
  bulkStageMoveResultSchema,
  kanbanBoardSchema,
  stageConfigurationSchema,
  stageHistorySchema,
  stageMoveResultSchema,
  type BulkStageMoveResult,
  type GarmentStageFlowView,
  type KanbanBoard,
  type KanbanDueFilter,
  type StageConfigurationView,
  type StageHistory,
  type StageMoveResult,
  type WorkflowStage,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every workflow call the board makes.
 *
 * Each response is parsed against the shared schema, so the browser and the API cannot drift
 * apart about what a card is — which matters more here than anywhere else in the app, because
 * the board applies moves optimistically and has to rebuild a card from its own copy when the
 * server says no.
 */

export interface BoardParams {
  due?: KanbanDueFilter;
  customerId?: string;
  garmentTypeKey?: string;
  q?: string;
  priorityOnly?: boolean;
  limit?: number;
}

export async function getBoard(client: ApiClient, params: BoardParams = {}): Promise<KanbanBoard> {
  return await client.get('/kanban', {
    schema: kanbanBoardSchema,
    query: {
      due: params.due ?? 'all',
      ...(params.customerId ? { customerId: params.customerId } : {}),
      ...(params.garmentTypeKey ? { garmentTypeKey: params.garmentTypeKey } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.priorityOnly ? { priorityOnly: 'true' } : {}),
      ...(params.limit ? { limit: params.limit } : {}),
    },
  });
}

/** The shop's stage names and which stages it uses. Every screen reads them from here. */
export async function getStageConfiguration(client: ApiClient): Promise<StageConfigurationView> {
  return await client.get('/settings/stages', { schema: stageConfigurationSchema });
}

/** One stage as the workflow screen saves it: on or off, and what this shop calls it. */
export interface StageSettingInput {
  key: WorkflowStage;
  enabled: boolean;
  /** `null` puts the product's own name back. */
  label: string | null;
}

/** Replace the shop's workflow: every stage, switched and named. */
export async function updateStageSettings(
  client: ApiClient,
  stages: readonly StageSettingInput[],
): Promise<StageConfigurationView> {
  return await client.put('/settings/stages', { stages }, { schema: stageConfigurationSchema });
}

/**
 * Replace every per-garment flow at once.
 *
 * The whole list, not one garment's: the screen edits them together, and a per-garment PATCH
 * would make "this garment no longer has a flow of its own" a second endpoint rather than
 * simply leaving it out of the list.
 */
export async function updateStageFlows(
  client: ApiClient,
  flows: readonly GarmentStageFlowView[],
): Promise<StageConfigurationView> {
  return await client.put('/settings/stage-flows', { flows }, { schema: stageConfigurationSchema });
}

export interface StageMoveRequestBody {
  toStage: WorkflowStage;
  reason?: string | null;
  rework?: boolean;
  /**
   * The stage this client believed the garment was at. The server answers 409 when somebody
   * else moved it first — which is also what makes a queued write replayed after a reconnect
   * fail loudly instead of advancing the same garment twice.
   */
  expectedStage?: WorkflowStage;
}

/** The path both the direct call and the offline queue use. One definition, so they cannot differ. */
export function stageMovePath(orderId: string, itemId: string): string {
  return `/orders/${orderId}/items/${itemId}/stage`;
}

export async function moveItemStage(
  client: ApiClient,
  orderId: string,
  itemId: string,
  body: StageMoveRequestBody,
): Promise<StageMoveResult> {
  return await client.post(stageMovePath(orderId, itemId), body, {
    schema: stageMoveResultSchema,
  });
}

export async function bulkAdvance(
  client: ApiClient,
  itemIds: string[],
): Promise<BulkStageMoveResult> {
  return await client.post('/kanban/advance', { itemIds }, { schema: bulkStageMoveResultSchema });
}

export async function deliverOrder(
  client: ApiClient,
  orderId: string,
  itemIds?: string[],
): Promise<BulkStageMoveResult> {
  return await client.post(`/orders/${orderId}/deliver`, itemIds ? { itemIds } : {}, {
    schema: bulkStageMoveResultSchema,
  });
}

export async function getItemHistory(
  client: ApiClient,
  orderId: string,
  itemId: string,
): Promise<StageHistory> {
  return await client.get(`/orders/${orderId}/items/${itemId}/history`, {
    schema: stageHistorySchema,
  });
}
